import PropTypes from 'prop-types';
import { lazy } from 'react';
import {
  BrowserRouter,
  Route,
  Routes as ReactRouterRoutes,
} from 'react-router-dom';

import GenericErrorBanner from 'hb-react/publicoffice/components/GenericErrorBanner';
import { NoMatch } from 'hb-react/publicoffice/components/Router/NoMatch';
import { ROUTE_TAG_FEATURES } from 'hb-react/publicoffice/constants/routeTags';
import PublicOfficeApp from 'hb-react/publicoffice/providers/App';
import useNetworkConfiguration from 'hb-react/publicoffice/utils/useNetworkConfiguration';
import { EvErrorBoundary } from 'hb-react/shared/components/ErrorBoundary';

const EventsApp = lazy(() => import('./EventsApp'));

// Constants to prevent re-creation on every render
const NETWORK_CONFIGURATION_OPTIONS = ['stripe_sca'];
const NETWORK_ROUTE_TAGS = [
  {
    routeTag: ROUTE_TAG_FEATURES,
    params: { app_names: ['events', 'cookies'] },
  },
];

const EventsAppStandalone = ({
  topicId,
  page,
  page_title: pageTitle,
  title,
  networkLocation,
}) => {
  const { networkConfiguration, isError } = useNetworkConfiguration({
    networkConfigurationOptions: NETWORK_CONFIGURATION_OPTIONS,
    networkRouteTags: NETWORK_ROUTE_TAGS,
  });

  if (isError) {
    return <GenericErrorBanner />;
  }

  if (networkConfiguration) {
    const { colors, theming, styles, networkConfigData, featuresData } =
      networkConfiguration;

    const { features } = featuresData || {};

    const customNetwork = {
      network_configuration: {
        ...networkConfigData,
        events: features?.events?.config,
      },
      features,
      colors: colors.colors,
      page_title: pageTitle,
      styles: styles.styles,
      theming: theming?.theming,
      title,
    };

    const config = {
      custom: {
        page,
      },
      routes: {
        event_path(eventId, params) {
          if (topicId) {
            return Routes.topic_publicoffice_topics_network_event_path(
              eventId,
              {
                topic_id: topicId,
                ...params,
              },
            );
          }

          return Routes.network_event_path(eventId, {
            topic_id: topicId,
            ...params,
          });
        },
        event_registration_path(eventId, params) {
          if (topicId) {
            return Routes.topic_network_event_registration_path(eventId, {
              topic_id: topicId,
              ...params,
            });
          }

          return Routes.network_event_registration_path(eventId, params);
        },
        event_attendees_path(eventId, params) {
          return Routes.network_event_attendees_path(eventId, {
            topic_id: topicId,
            ...params,
          });
        },
        summary_ticket_event_attendees_path(eventId, params) {
          return Routes.summary_ticket_network_event_attendees_path(eventId, {
            topic_id: topicId,
            ...params,
          });
        },
      },
      networkLocation,
    };

    const isWLTopic =
      window.Hivebrite?.topic?.whitelabelURL &&
      window.Hivebrite?.topic?.whitelabelName === window.location.host;

    const basename = topicId && !isWLTopic ? `/topics/${topicId}` : '/';

    return (
      <EvErrorBoundary>
        <PublicOfficeApp network={customNetwork}>
          <BrowserRouter basename={basename}>
            <ReactRouterRoutes>
              <Route path="events/*" element={<EventsApp config={config} />} />
              <Route path="*" element={<NoMatch />} />
            </ReactRouterRoutes>
          </BrowserRouter>
        </PublicOfficeApp>
      </EvErrorBoundary>
    );
  }

  return null;
};

EventsAppStandalone.propTypes = {
  topicId: PropTypes.string,
};

export default EventsAppStandalone;
